import type { TravellerData } from "../../../ExcelUpload/ExcelUpload.types";
import {
  HEADER_MAPPING,
  GENDER_VALUE_MAPPING,
} from "../../../ExcelUpload/ExcelUpload.constants";
import { parseDateToYYYYMMDD } from "../../../ExcelUpload/ExcelUpload.utils";

export interface ParseResult {
  unique: TravellerData[];
  totalParsed: number;
  duplicatesRemoved: number;
}

export interface PlaceholderValues {
  X: number;
  Y: number;
  Z: number;
  W: number;
  T: number;
  unitPrice: number;
}

export function formatCurrency(amount: number): string {
  return "₹" + amount.toLocaleString("en-IN");
}

export function replacePlaceholders(template: string | undefined, v: PlaceholderValues): string {
  if (!template) return "";
  let result = template;
  result = result.replace(/\{\{(\w+)\}\*\{unitPrice\}\}/g, (_match, key) => {
    const count = (v as any)[key];
    if (count != null && v.unitPrice) return formatCurrency(Number(count) * v.unitPrice);
    return _match;
  });
  result = result
    .replace(/\{X\}/g, String(v.X))
    .replace(/\{Y\}/g, String(v.Y))
    .replace(/\{Z\}/g, String(v.Z))
    .replace(/\{W\}/g, String(v.W))
    .replace(/\{T\}/g, String(v.T));
  return result;
}

export function replaceInObj(obj: any, v: PlaceholderValues): any {
  if (typeof obj === "string") return replacePlaceholders(obj, v);
  if (Array.isArray(obj)) return obj.map((item) => replaceInObj(item, v));
  if (obj && typeof obj === "object") {
    const out: Record<string, unknown> = {};
    for (const k of Object.keys(obj)) out[k] = replaceInObj(obj[k], v);
    return out;
  }
  return obj;
}

export const ERROR_CODE_MAP: Record<string, string> = {
  unable_to_read_file: "unable_to_read_file",
  no_details_found: "no_details_found",
  file_size_exceeds: "file_size_exceeds",
  unsupported_file_type: "unsupported_file_type",
};

export function excelDataToFormTravellers(data: TravellerData[], limit: number) {
  return data.slice(0, limit).map((t) => ({
    name: t.name || "",
    dob: t.dob || "",
    gender: t.gender ? t.gender.charAt(0).toUpperCase() + t.gender.slice(1).toLowerCase() : "",
    passport: t.passport || "",
    nomineeName: t.nomineeName || "",
    isSelected: true,
    isEdited: true,
    isSavedTraveller: false,
    isExcelUploaded: true,
  }));
}

/** Max Excel file size to embed as data URL across reload (sessionStorage quota). */
export const PAX_RELOAD_MAX_FILE_EMBED_BYTES = 1.5 * 1024 * 1024;

export async function parseExcelFile(
  file: File,
  maxRows: number,
  requiredFields?: (keyof TravellerData)[],
): Promise<ParseResult> {
  const XLSX = await import("xlsx");
  const arrayBuffer = await file.arrayBuffer();
  const workbook = XLSX.read(arrayBuffer, { type: "array" });

  const firstSheetName = workbook.SheetNames[0];
  if (!firstSheetName) throw new Error("unable_to_read_file");

  const worksheet = workbook.Sheets[firstSheetName];
  const jsonData = XLSX.utils.sheet_to_json<string[]>(worksheet, { header: 1 });
  if (jsonData.length < 2) throw new Error("no_details_found");

  const headers = jsonData[0] as string[];
  const headerMap = new Map<number, keyof TravellerData>();
  const foundFields = new Set<keyof TravellerData>();
  headers.forEach((h, i) => {
    const key = HEADER_MAPPING[h?.toLowerCase().trim() || ""];
    if (key && !foundFields.has(key)) { headerMap.set(i, key); foundFields.add(key); }
  });

  if (requiredFields) {
    const missing = requiredFields.filter((f) => !foundFields.has(f));
    if (missing.length) throw new Error(`fields_missing:${missing.join(",")}`);
  }

  const dataRows = jsonData.slice(1, 1 + maxRows);
  const seen = new Set<string>();
  const all: TravellerData[] = [];
  const unique: TravellerData[] = [];
  let duplicatesRemoved = 0;

  for (const row of dataRows) {
    if (!row?.length || row.every((c) => !c)) continue;
    const entry: Partial<TravellerData> = {};
    let has = false;
    headerMap.forEach((field, col) => {
      const cell = row[col];
      if (field === "dob") {
        const v = parseDateToYYYYMMDD(cell); entry[field] = v; if (v) has = true;
      } else if (field === "gender") {
        const raw = cell != null ? String(cell).trim().toLowerCase() : "";
        const v = GENDER_VALUE_MAPPING[raw] || raw.toUpperCase(); entry[field] = v; if (v) has = true;
      } else {
        const v = cell != null ? String(cell).trim() : ""; entry[field] = v; if (v) has = true;
      }
    });
    if (!has) continue;
    all.push(entry as TravellerData);
    const pp = (entry as TravellerData).passport?.trim() || "";
    if (pp && seen.has(pp)) { duplicatesRemoved++; continue; }
    if (pp) seen.add(pp);
    unique.push(entry as TravellerData);
  }

  if (!all.length) throw new Error("no_details_found");
  return { unique, totalParsed: all.length, duplicatesRemoved };
}
